import { useContext } from 'react';

import { LoadingContext, ShownError, showErrorMessage } from '../components/appPane';
import { LoginContext } from '../App';

import { LoginService, LoginStrageInfo } from '../logic/loginService';
import { MESSAGES, LAMBDA_URL } from '../constants/const';

export const REQ_API_LOGIN: string = `${LAMBDA_URL}canLoginOperationPlan`;
export const REQ_API_LOGOUT: string = `${LAMBDA_URL}logoutOperationPlan`;
export const REQ_DRAFT_CONDITION: string = `${LAMBDA_URL}draftCondition`;
export const REQ_ORIGIN_SETTING: string = `${LAMBDA_URL}originSetting`;
export const REQ_PLAN_RESULT: string = `${LAMBDA_URL}operationPlanResult`;
export const REQ_PAST_RESULT: string = `${LAMBDA_URL}operationPastResult`;

export const SESSION_ID: string = 'sessionId';
export const USER_ID: string = 'userId';

interface APIRequestOptions {
  method: string; // 'GET', 'POST', 'PUT', 'DELETE', etc.
  headers: HeadersInit; // 任意のリクエストヘッダー
  body?: BodyInit; // 任意のリクエストボディ
}

let requestCount = 0;
export type JsonResponse = { [key: string]: any };
export type TextResponse = string;
export type BinaryResponse = Blob;
type APIResponseType = TextResponse | JsonResponse;

interface APIResponse<T extends APIResponseType> {
  status: number;
  payload: T;
}

export function useApi(window?: Window) {
  const { setLoading } = useContext(LoadingContext);
  const { setIsLogin } = useContext(LoginContext);
  const pendingRequests: { [key: number]: boolean } = {};

  //非公開
  async function apiRequest<T extends APIResponseType>(url: string, options: APIRequestOptions): Promise<APIResponse<T>> {
    const requestId = ++requestCount % 10000;
    pendingRequests[requestId] = true;

    setLoading(true);

    const promise = fetch(url, {
      method: options.method,
      headers: options.headers,
      body: options.body,
    }).then(async (response) => {
      const contentType = response.headers.get('content-type') || '';
      let status = response.status;
      let message; // text/plain
      let jsonObj; // application/json
      let binary; // other

      if (/text\/plain/.test(contentType)) {
        message = await response.text();
      } else if (/application\/json/.test(contentType)) {
        jsonObj = await response.json();
        status = jsonObj.statusCode || response.status;
      } else {
        // バイナリ
        binary = await response.blob();
      }

      const apiResponse = { status, payload: message || jsonObj || binary };

      console.log(`api response`, apiResponse);

      return apiResponse;
    });

    const dissmissLoading = new Promise<void>((resolve) => {
      setTimeout(() => {
        promise.finally(() => {
          delete pendingRequests[requestId];
          if (setLoading && Object.keys(pendingRequests).length === 0) {
            setLoading(false);
            resolve();
          }
        });
      });
    });

    const { status, payload } = await promise;

    if (status === 401) {
      showErrorMessage(MESSAGES.ERROR_SESSION_TIMEOUT, { window }).then(() => {
        LoginService.removeLoginInfo();
        setIsLogin(false);
      });
    } else if (status !== 200) {
      const errorMessage = (typeof payload === 'string' ? payload : payload.errorMessage) || MESSAGES.ERROR_SERVER;

      // ローディング非表示後にエラーメッセージ表示
      dissmissLoading.then(() => {
        showErrorMessage(errorMessage, { window });
      });

      // 成功ダイアログメッセージ表示待機中の可能性があるので例外をスローしてキャンセル
      throw new ShownError(errorMessage);
    }

    return Promise.all([promise, dissmissLoading]).then(([response]) => response);
  }

  /**
   *
   * 指定されたプロミスが解決されるまでローディングインジケータを表示する
   *
   */
  const showProgress = async function <T>(promise: () => Promise<T>) {
    setLoading(true);
    return new Promise<T>((resolve, reject) => {
      requestAnimationFrame(async () => {
        try {
          const result = await promise();
          resolve(result);
        } catch (e) {
          reject(e);
        } finally {
          requestAnimationFrame(() => {
            setLoading(false);
          });
        }
      });
    });
  };

  function defineHeaders(): Headers {
    // セッションストレージからIDを取得する
    const loginInfo: LoginStrageInfo = LoginService.getLoginInfo();

    const retHeaders: Headers = new Headers();
    retHeaders.append('Authorization', 'Bearer your-token-here');
    retHeaders.append('Accept', 'application/json');
    if (loginInfo.sessionId !== '') {
      retHeaders.append(SESSION_ID, loginInfo.sessionId);
      retHeaders.append(USER_ID, String(loginInfo.userId));
    }
    return retHeaders;
  }

  //公開
  const getRequest = async <T extends APIResponseType>(url: string): Promise<APIResponse<T>> => {
    const pushHeaders: Headers = defineHeaders();
    pushHeaders.append('Content-Type', 'application/json');
    const option: APIRequestOptions = {
      method: 'GET',
      headers: pushHeaders,
    };
    return apiRequest(url, option);
  };

  const postRequestJson = async <T extends APIResponseType>(url: string, bodyObj: any): Promise<APIResponse<T>> => {
    const pushHeaders: Headers = defineHeaders();
    pushHeaders.append('Content-Type', 'application/json');
    const option: APIRequestOptions = {
      method: 'POST',
      headers: pushHeaders,
      body: JSON.stringify(bodyObj),
    };
    return apiRequest(url, option);
  };

  const postRequestBinary = async <T extends APIResponseType>(url: string, binaryBody: Uint8Array): Promise<APIResponse<T>> => {
    const pushHeaders: Headers = defineHeaders();
    pushHeaders.append('Content-Type', 'multipart/form-data');

    const option: APIRequestOptions = {
      method: 'POST',
      headers: pushHeaders,
      body: binaryBody,
    };

    return apiRequest(url, option);
  };

  return {
    showProgress,
    getRequest,
    postRequestJson,
    postRequestBinary,
  };
}
